import React, { useRef, useEffect } from 'react';
import {
    Box,
    Toolbar,
    Typography,
    Paper,
    TextField,
    IconButton,
    Tabs,
    Tab,
    Divider,
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TableHead,
    Grid,
    FormControl,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import { convertTimeToMinutes, formatTimeWithPeriod, useWorkingHourController } from './workingHourController';
import Overrides from './overridesDates';
import { format } from 'date-fns';
import TimezoneSelector from '../../util/timezoneSelector';
import {
    ConvertLocal12To24Hour,
    ConvertUTCToLocal12Hour,
    GetSortedWorkingHours
} from '../../util/workingHour';
import Holiday from './business/holiday';
import { EntityType } from '../../redux/actions/businessActions';
import { StyledBox } from '../../assets/css/styledComponent';

const WorkingHourView = (props) => {
    const {
        workingHourGroup,
        hasErrors,
        handleChange,
        timeRangeErrors,
        handleAddRow,
        handleDeleteRow,
        // Assuming you can update rows via this function (or implement your own)
        updateWorkingHoursRow,
        getWorkingHours,
        getWorkingHourOverrides,
        getWorkingHourHolidays,
        handleTimezoneSelect,
        workingHourOverrideGroups,
        refetchOverrides,
        handleDeleteWorkingHourGroup
    } = useWorkingHourController(props);

    // Sorted working hours based on the group's timezone
    const sortedWorkingHours = GetSortedWorkingHours(
        workingHourGroup?.workingHours || [],
        workingHourGroup?.timeZone
    );
    const overridesRef = useRef(null);
    const [tabValue, setTabValue] = React.useState(0);

    // States for copy row dialog functionality
    const [copyDialogOpen, setCopyDialogOpen] = React.useState(false);
    const [rowToCopy, setRowToCopy] = React.useState(null);
    const [selectedCopyDay, setSelectedCopyDay] = React.useState('');

    // Handle tab changes and fetch corresponding data
    const handleChangeTab = (event, newValue) => {
        setTabValue(newValue);
    };

    useEffect(() => {
        if (tabValue === 0) {
            getWorkingHours();
        } else if (tabValue === 1) {
            getWorkingHourOverrides();
        } else if (tabValue === 2) {
            getWorkingHourHolidays();
        }
    }, [tabValue]);

    // Component for time input fields for both parent and child rows
    const TimeInputFields = ({
        startTime,
        endTime,
        hasError,
        onChangeStart,
        onChangeEnd
    }) => {
        const formattedStartTime = ConvertLocal12To24Hour(startTime, workingHourGroup?.timeZone);
        const formattedEndTime = ConvertLocal12To24Hour(endTime, workingHourGroup?.timeZone);

        return (
            <Box display="flex" alignItems="center">
                <TextField
                    type="time"
                    value={formattedStartTime || ""}
                    onChange={(e) => onChangeStart(e)}
                    error={hasError}
                    helperText={hasError ? "Invalid time range" : ""}
                    fullWidth
                    InputProps={{ sx: { maxWidth: '120px' } }}
                    sx={{
                        borderRadius: '8px',
                        '& .MuiInputBase-root': { color: '#333' },
                        '& .Mui-error': { color: 'red' },
                        '& .Mui-focused': { borderColor: '#4caf50' },
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#ccc', borderRadius: '8px' },
                            '&:hover fieldset': { borderColor: '#888' },
                            '&.Mui-focused fieldset': { borderColor: '#4caf50' }
                        }
                    }}
                />
                <Box sx={{ mx: 1 }}> - </Box>
                <TextField
                    type="time"
                    value={formattedEndTime || ""}
                    onChange={(e) => onChangeEnd(e)}
                    error={hasError}
                    helperText={hasError ? "Invalid time range" : ""}
                    fullWidth
                    InputProps={{ sx: { maxWidth: '120px' } }}
                    sx={{
                        borderRadius: '8px',
                        '& .MuiInputBase-root': { color: '#333' },
                        '& .Mui-error': { color: 'red' },
                        '& .Mui-focused': { borderColor: '#4caf50' },
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#ccc', borderRadius: '8px' },
                            '&:hover fieldset': { borderColor: '#888' },
                            '&.Mui-focused fieldset': { borderColor: '#4caf50' }
                        }
                    }}
                />
            </Box>
        );
    };

    // Function to open the copy row dialog and store row data to copy
    const openCopyDialog = (parent, children) => {
        setRowToCopy({ parent, children });
        setCopyDialogOpen(true);
    };
    const cleanWorkingHour = (record) => {
        const { __typename, ...rest } = record;
        let cleaned = { ...rest };
        if (cleaned.id !== undefined && cleaned.id !== null) {
            cleaned.id = Number(cleaned.id);
        }
        if (cleaned.groupId !== undefined && cleaned.groupId !== null) {
            cleaned.groupId = Number(cleaned.groupId);
        }
        // For parent rows, parentId should be null (and for child rows, it will be set later).
        if (cleaned.parentId !== undefined && cleaned.parentId !== null) {
            cleaned.parentId = Number(cleaned.parentId);
        }
        return cleaned;
    };

    // Function to handle copy confirmation.
    // For each target day:
    // - If a row exists for that day, update its fields using the copied parent data,
    //   preserving its existing day and id.
    // - Replace its children with copies of the source children, setting their parentId to the target parent's id.
    // - If no row exists, add a new row as before.
    const handleCopyRowConfirm = () => {
        if (!rowToCopy || !selectedCopyDay) return;

        // Clean the source parent and children.
        const sourceParent = cleanWorkingHour(rowToCopy.parent);
        // For children, also remove the 'day' field.
        const sourceChildren = rowToCopy.children.map((child) => {
            const { day, ...rest } = cleanWorkingHour(child);
            return rest;
        });

        // Determine target days.
        const daysToApply =
            selectedCopyDay === "All"
                ? ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]
                : [selectedCopyDay];

        // Build an array of only the updated/new rows (delta changes).
        let deltaRows = [];

        daysToApply.forEach((day) => {
            // Skip processing if the target day is the same as the source row's day.
            if (day === sourceParent.day) return;

            // Look for an existing parent row for the target day (parent row: parentId === null).
            const existingParent = workingHourGroup.workingHours.find(
                (row) =>
                    cleanWorkingHour(row).day === day &&
                    cleanWorkingHour(row).parentId === null
            );

            if (existingParent) {
                const targetRow = cleanWorkingHour(existingParent);
                // Build an updated parent: keep target row's id, groupId, and day,
                // but override onJob, startTime, and endTime from the source parent,
                // reformatting times as needed.
                const updatedParent = {
                    ...targetRow,
                    onJob: sourceParent.onJob,
                    startTime: formatTimeWithPeriod(convertTimeToMinutes(sourceParent.startTime)),
                    endTime: formatTimeWithPeriod(convertTimeToMinutes(sourceParent.endTime)),
                    id: Number(targetRow.id),
                    groupId: Number(targetRow.groupId),
                    parentId: null,
                };
                // Push the updated parent.
                deltaRows.push(updatedParent);

                // Create new child rows from the source children without an id.
                const newChildRows = sourceChildren.map((child) => {
                    const { id, ...childWithoutId } = child; // remove id
                    return {
                        ...childWithoutId,
                        groupId: Number(targetRow.groupId),
                        onJob: child.onJob,
                        startTime: formatTimeWithPeriod(convertTimeToMinutes(child.startTime)),
                        endTime: formatTimeWithPeriod(convertTimeToMinutes(child.endTime)),
                        parentId: Number(targetRow.id),
                    };
                });

                // Push the new child rows.
                deltaRows.push(...newChildRows);
            } else {
                // No target parent row exists for this day—create a new one.
                const newParent = {
                    ...sourceParent,
                    day: day, // Set to target day.
                    groupId: Number(sourceParent.groupId),
                    parentId: null,
                    startTime: formatTimeWithPeriod(convertTimeToMinutes(sourceParent.startTime)),
                    endTime: formatTimeWithPeriod(convertTimeToMinutes(sourceParent.endTime)),
                };
                // Omit the id so that the backend will generate it.
                if (newParent.id !== undefined) {
                    delete newParent.id;
                }
                // Create new child rows for the new parent.
                const newChildRows = sourceChildren.map((child) => {
                    const { id, day, ...childWithoutId } = child;
                    return {
                        ...childWithoutId,
                        groupId: Number(sourceParent.groupId),
                        onJob: child.onJob,
                        startTime: formatTimeWithPeriod(convertTimeToMinutes(child.startTime)),
                        endTime: formatTimeWithPeriod(convertTimeToMinutes(child.endTime)),
                        parentId: null, // new parent's id will be generated and linked later
                    };
                });
                // Push the new parent and its child rows.
                deltaRows.push(newParent, ...newChildRows);
            }
        });

        // Pass only the updated/new rows (deltaRows) to the backend.
        updateWorkingHoursRow(deltaRows);

        // Reset dialog state.
        setCopyDialogOpen(false);
        setSelectedCopyDay("");
        setRowToCopy(null);
    }; 

    // Component to render each row (parent and its child rows)
    const TimeRowData = ({ parent, children }) => {
        const { day, startTime, endTime, onJob, id } = parent;
        if (!onJob && day === "") return null;

        return (
            <>
                {/* Parent Row */}
                <StyledBox display="flex" alignItems="center" marginBottom={2} flexWrap={onJob ? 'wrap' : 'nowrap'} >
                    <StyledBox width="7%" minWidth="40px" marginBottom={1}>
                        {day && (
                            <Checkbox
                                checked={onJob}
                                onChange={(e) =>
                                    handleChange(parent, children, parent, e, "onJob", workingHourGroup?.timeZone)
                                }
                            />
                        )}
                    </StyledBox>
                    <StyledBox width="5%" minWidth="80px" marginBottom={1}>
                        {day && <Typography fontWeight="bold">{day?.slice(0, 3).toUpperCase()}</Typography>}
                    </StyledBox>
                    <StyledBox
                        width="80%"
                        minWidth="20px"
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        {onJob ? (
                            <StyledBox display="flex" alignItems="center" gap={2} width="100%">
                                <TimeInputFields
                                    startTime={startTime}
                                    endTime={endTime}
                                    hasError={timeRangeErrors.find(err => err.id === parent.id)?.message}
                                    onChangeStart={(e) =>
                                        handleChange(parent, children, parent, e, "startTime", workingHourGroup?.timeZone)
                                    }
                                    onChangeEnd={(e) =>
                                        handleChange(parent, children, parent, e, "endTime", workingHourGroup?.timeZone)
                                    }
                                />
                                {/* Add Row Button */}
                                <IconButton
                                    size="small"
                                    onClick={() => handleAddRow(parent, children, workingHourGroup?.timeZone)}
                                    aria-label="add"
                                >
                                    <AddIcon />
                                </IconButton>
                                {/* Copy Row Button */}
                                <IconButton
                                    size="small"
                                    onClick={() => openCopyDialog(parent, children)}
                                    aria-label="copy"
                                >
                                    <ContentCopyIcon />
                                </IconButton>
                            </StyledBox>
                        ) : (
                            <Typography color="textSecondary" style={{ opacity: 0.5 }}>
                                Off Work
                            </Typography>
                        )}
                    </StyledBox>
                </StyledBox>

                {/* Child Rows */}
                {onJob &&
                    children.map((child, childIndex) => (
                        <StyledBox
                            display="flex"
                            alignItems="center"
                            marginBottom={2}
                            flexWrap="wrap"
                            key={`${id}-child-${childIndex}`}
                        >
                            <StyledBox width="17%" minWidth="100px" marginBottom={1} />
                            <StyledBox
                                width="70%"
                                minWidth="100px"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <StyledBox display="flex" alignItems="center" gap={2} width="100%">
                                    <TimeInputFields
                                        startTime={child.startTime}
                                        endTime={child.endTime}
                                        hasError={timeRangeErrors.find(err => err.id === child.id || err.id === "new")?.message}
                                        onChangeStart={(e) =>
                                            handleChange(parent, children, child, e, "startTime", workingHourGroup?.timeZone)
                                        }
                                        onChangeEnd={(e) =>
                                            handleChange(parent, children, child, e, "endTime", workingHourGroup?.timeZone)
                                        }
                                    />
                                    <IconButton
                                        size="small"
                                        onClick={() => handleDeleteRow(child, parent.groupId)}
                                        aria-label="delete"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </StyledBox>
                                {hasErrors && (
                                    <Typography color="error">
                                        {timeRangeErrors.find(err => err.id === child.id || err.id === "new")?.message}
                                    </Typography>
                                )}
                            </StyledBox>
                        </StyledBox>
                    ))}
                <Divider sx={{ my: 2 }} />
            </>
        );
    };

    // Format date helper function
    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            if (typeof dateString === 'number' || /^\d+$/.test(dateString)) {
                return format(new Date(parseInt(dateString)), 'MMM dd, yyyy');
            }
            return format(new Date(dateString), 'MMM dd, yyyy');
        } catch (error) {
            console.error('Error formatting date:', error, dateString);
            return 'Invalid Date';
        }
    };

    // Tab configuration for the various sections
    const tabConfig = [
        {
            label: 'Weekly Hours',
            show: true,
            content: (
                <StyledBox sx={{ marginTop: '15px' }}>
                    {/* Timezone Selector */}
                    <Box sx={{ borderRadius: 1 }}>
                        <Grid alignItems="center">
                            <Grid container alignItems="center" spacing={0}>
                                <Grid item xs>
                                    <FormControl fullWidth>
                                        <TimezoneSelector
                                            timezoneId={workingHourGroup?.timeZone?.id}
                                            onTimezoneSelect={handleTimezoneSelect}
                                            isDisabled={props.isDisabled}
                                        />
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Box>
                    {sortedWorkingHours?.map(({ parent, children }) => (
                        <TimeRowData key={parent.id} parent={parent} children={children} />
                    ))}
                </StyledBox>
            )
        },
        {
            label: 'Overrides',
            show: props.entityType === EntityType.STAFF && !props.hideOverride,
            content: (
                <StyledBox sx={{ p: 2 }}>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        Define specific dates that you want to override and be available on.
                    </Typography>
                    <Overrides
                        ref={overridesRef}
                        staffData={props.staffData}
                        refetchOverrides={refetchOverrides}
                        providerUser={props.providerUser}
                    />
                    {workingHourOverrideGroups && workingHourOverrideGroups?.getWorkingHourOverrideGroups?.length > 0 ? (
                        <TableContainer component={Paper} sx={{ mt: 3 }}>
                            <Table aria-label="time off periods">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Title</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Start Date  - End Date</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}></TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {workingHourOverrideGroups.getWorkingHourOverrideGroups.map((group) => (
                                        <TableRow key={group.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                            <TableCell>{group.title}</TableCell>
                                            <TableCell>
                                                <div>
                                                    {formatDate(group.startDate)} - {formatDate(group.endDate)}
                                                </div>
                                                {group.workingHourOverrides.map((override, index) => {
                                                    const displayStartTime =
                                                        override.startTime.trim().toLowerCase() === 'all day'
                                                            ? '- All day off work'
                                                            : ConvertUTCToLocal12Hour(override.startTime, props.providerUser?.timeZone);
                                                    const displayEndTime =
                                                        override.endTime.trim().toLowerCase() === 'all day'
                                                            ? ''
                                                            : ConvertUTCToLocal12Hour(override.endTime, props.providerUser?.timeZone);
                                                    return (
                                                        <div key={index}>
                                                            {displayStartTime} - {displayEndTime}
                                                        </div>
                                                    );
                                                })}
                                            </TableCell>
                                            <TableCell>
                                                <IconButton onClick={() => overridesRef.current?.handleOpen(group)}>
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton onClick={() => handleDeleteWorkingHourGroup(group.id)}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Typography sx={{ mt: 3, textAlign: 'center', color: 'text.secondary' }}>
                            No overrides defined
                        </Typography>
                    )}
                </StyledBox>
            )
        },
        {
            label: 'Holidays',
            show: props.entityType === EntityType.BUSINESS,
            content: (
                <StyledBox sx={{ p: 2 }}>
                    <Holiday />
                </StyledBox>
            )
        }
    ];

    // Filter out hidden tabs while preserving order
    const visibleTabs = tabConfig.filter(tab => tab.show);

    return (
        <StyledBox
            sx={{
                maxWidth: '800px', 
                opacity: props.isDisabled ? 0.6 : 1,
          }}
        >
          <Toolbar>
                <Typography variant="h6">
                    Set your {props.isCustom ? "custom" : "default"} availability schedule
                </Typography>
            </Toolbar>
          <StyledBox elevation={0} sx={{ padding: 2 }}>
                <>
                    <Tabs value={tabValue} onChange={handleChangeTab} aria-label="business working hours tabs">
                        {visibleTabs?.map((tab, index) => (
                            <Tab key={index} label={tab.label} />
                        ))}
                    </Tabs>
                    {/* Render content for the active tab */}
                    <Box>{visibleTabs[tabValue]?.content}</Box>
                </>
          </StyledBox>

            {/* Copy Row Dialog */}
            <Dialog open={copyDialogOpen} onClose={() => setCopyDialogOpen(false)}>
                <DialogTitle>Copy Working Hours Row</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Select the day to which you want to update (replace) the existing row with the copied row. Choose "All Days" to update for every day.
                    </DialogContentText>
                    <TextField
                        select
                        label="Day"
                        fullWidth
                        value={selectedCopyDay}
                        onChange={(e) => setSelectedCopyDay(e.target.value)}
                        SelectProps={{ native: true }}
                        variant="outlined"
                        sx={{ mt: 2 }}
                    >
                        <option value="">Select a day</option>
                        <option value="All">All Days</option>
                        <option value="Monday">Monday</option>
                        <option value="Tuesday">Tuesday</option>
                        <option value="Wednesday">Wednesday</option>
                        <option value="Thursday">Thursday</option>
                        <option value="Friday">Friday</option>
                        <option value="Saturday">Saturday</option>
                        <option value="Sunday">Sunday</option>
                    </TextField>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCopyDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleCopyRowConfirm} disabled={!selectedCopyDay}>
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>
        </StyledBox>
    );
};

export default WorkingHourView;
